// ClipRadar website — the mid-page accent band.
//
// This is the one full-bleed colour surface on the site: the break in an
// otherwise unbroken dark scroll, and the place to convert between the
// comparison and Pricing.
//
// Two rules shaped it:
//  - The glacier system uses the accent as LIGHT, not paint. So the band is a
//    lit horizon, and the light physically comes from the ruler line below.
//  - It must not be a third centred headline/sub/button stack — the tester
//    quote above it and Pricing below it are both centred. It is left-aligned.
//
// The signature is the ALLOWANCE RULER: forty ticks, majors every ten, drawn
// like the ruler above a timeline. It is not decoration — each tick is one of
// the forty free searches, so the graphic states the offer instead of a
// sentence restating it. It lights left-to-right once, when it comes into view.
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { GradBtn } from '../components/primitives';
import { CRWIcon } from '../components/icons';

const FREE_SEARCHES = 40;

export function AccentCTA() {
  const ref = useRef<HTMLElement | null>(null);
  const [lit, setLit] = useState(false);

  // one-shot: the ruler lights as the band arrives, then stays lit
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) { setLit(true); return; }
    const io = new IntersectionObserver(
      (entries) => { if (entries.some((e) => e.isIntersecting)) { setLit(true); io.disconnect(); } },
      { rootMargin: '-12% 0px -12% 0px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <section className="crw-acta" ref={ref} aria-labelledby="crw-acta-h" data-lit={lit || undefined}>
      <div className="crw-acta-bloom" aria-hidden="true"></div>

      <div className="crw-acta-in">
        <span className="crw-acta-eyebrow">Free trial</span>
        <h2 id="crw-acta-h" className="crw-acta-h">See it on your own footage.</h2>
        <p className="crw-acta-sub">
          Point ClipRadar at a folder you already have and search it.
        </p>

        {/* the allowance ruler — forty ticks, one per free search */}
        <div className="crw-acta-ruler" aria-hidden="true">
          {Array.from({ length: FREE_SEARCHES }, (_, i) => (
            // majors mark the 10th / 20th / 30th / 40th search, so the measure
            // also closes cleanly on its right edge
            <i key={i} style={{ ['--i' as string]: String(i) }} data-major={i % 10 === 9 ? '' : undefined} />
          ))}
        </div>
        <div className="crw-acta-scale">
          <span className="crw-acta-scale-lead"><b>{FREE_SEARCHES}</b> free searches</span>
          <span className="crw-acta-scale-quiet"><CRWIcon d="check" size={13} /> No card required</span>
        </div>

        <div className="crw-acta-ctas">
          <Link to="/trial" style={{ textDecoration: 'none', display: 'flex' }}>
            {/* cream, not MetalBtn: the metal button's dark glass core reads as a
                hole punched in the blue. The primary has to be the brightest
                object here — light you can press. */}
            <GradBtn size="lg" style={{ letterSpacing: '0.08em', fontFamily: 'var(--font-mono)', textTransform: 'uppercase' }}>Start free trial</GradBtn>
          </Link>
          <a href="#pricing" className="crw-acta-link">
            <span className="crw-acta-link-icon"><CRWIcon d="arrowRight" size={11} /></span>
            See pricing
          </a>
        </div>
      </div>
    </section>
  );
}
